import { useEffect, useState, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CATEGORIES } from "@/lib/categories";
import TipCard from "@/components/TipCard";
import { Loader2, PlusCircle, Filter, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

export default function Home() {
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const tabsRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = tabsRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = tabsRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [updateScrollState]);

  // First-load nudge to hint at more categories
  useEffect(() => {
    const el = tabsRef.current;
    if (!el) return;
    const canScroll = el.scrollWidth > el.clientWidth + 4;
    if (!canScroll) return;
    const t1 = setTimeout(() => el.scrollTo({ left: 60, behavior: "smooth" }), 500);
    const t2 = setTimeout(() => el.scrollTo({ left: 0, behavior: "smooth" }), 1300);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  const scrollBy = (dx) => {
    const el = tabsRef.current;
    if (!el) return;
    el.scrollBy({ left: dx, behavior: "smooth" });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tips", { params: category !== "all" ? { category } : {} });
      setTips(data.tips);
    } catch {
      toast.error("Could not load tips");
    }
    setLoading(false);
  }, [category]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-8">
      {/* Hero */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-sm font-semibold text-[#2A9D8F] tracking-widest uppercase">Community Feed</p>
          <h1 className="mt-2 font-heading text-4xl sm:text-5xl font-bold text-[#1B2A4A] leading-[1.05]">
            Tips from your <span className="text-[#E8621A]">neighbors.</span>
          </h1>
          <p className="mt-3 text-[#1B2A4A]/70 max-w-xl">
            Real ways real people are saving money. Share what works — help someone else's month go a little easier.
          </p>
        </div>
        <Link to="/post" className="hidden sm:block">
          <Button
            data-testid="home-post-cta"
            className="h-12 px-6 rounded-full bg-[#E8621A] hover:bg-[#D05615] text-white font-semibold transition-colors duration-200"
          >
            <PlusCircle className="w-4 h-4 mr-2" /> Share a tip
          </Button>
        </Link>
      </div>

      {/* Category filter */}
      <div className="relative -mx-4 sm:mx-0">
        <div
          ref={tabsRef}
          data-testid="category-tabs-scroll"
          className="flex gap-2 overflow-x-auto pb-2 px-10 sm:px-12 no-scrollbar scroll-smooth"
          style={{ scrollbarWidth: "none" }}
        >
          <button
            data-testid="filter-all"
            onClick={() => setCategory("all")}
            className={`flex items-center gap-1.5 shrink-0 rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
              category === "all" ? "bg-[#1B2A4A] text-white" : "bg-white text-[#1B2A4A]/80 border border-[#1B2A4A]/10 hover:bg-[#1B2A4A]/5"
            }`}
          >
            <Filter className="w-3.5 h-3.5" /> All
          </button>
          {CATEGORIES.map((c) => {
            const Icon = c.icon;
            const active = category === c.id;
            return (
              <button
                key={c.id}
                data-testid={`filter-${c.id}`}
                onClick={() => setCategory(c.id)}
                className={`flex items-center gap-1.5 shrink-0 rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
                  active ? "bg-[#1B2A4A] text-white" : "bg-white text-[#1B2A4A]/80 border border-[#1B2A4A]/10 hover:bg-[#1B2A4A]/5"
                }`}
                style={active ? {} : { color: c.color }}
              >
                <Icon className="w-3.5 h-3.5" /> {c.label}
              </button>
            );
          })}
        </div>

        {/* Left scroll indicator */}
        <motion.button
          type="button"
          onClick={() => scrollBy(-220)}
          data-testid="tabs-scroll-left"
          initial={false}
          animate={{ opacity: canScrollLeft ? 1 : 0, pointerEvents: canScrollLeft ? "auto" : "none" }}
          transition={{ duration: 0.2 }}
          aria-label="Scroll categories left"
          className="absolute left-0 top-0 bottom-2 flex items-center justify-center w-10 sm:w-12 pl-2 sm:pl-4 pr-1 bg-gradient-to-r from-[#F7F5F2] via-[#F7F5F2]/95 to-transparent"
        >
          <span className="flex items-center justify-center w-8 h-8 rounded-full bg-white shadow-sm border border-[#E8621A]/25 text-[#E8621A]">
            <ChevronLeft className="w-4 h-4" strokeWidth={2.75} />
          </span>
        </motion.button>

        {/* Right scroll indicator */}
        <motion.button
          type="button"
          onClick={() => scrollBy(220)}
          data-testid="tabs-scroll-right"
          initial={false}
          animate={{ opacity: canScrollRight ? 1 : 0, pointerEvents: canScrollRight ? "auto" : "none" }}
          transition={{ duration: 0.2 }}
          aria-label="Scroll categories right"
          className="absolute right-0 top-0 bottom-2 flex items-center justify-center w-10 sm:w-12 pr-2 sm:pr-4 pl-1 bg-gradient-to-l from-[#F7F5F2] via-[#F7F5F2]/95 to-transparent"
        >
          <motion.span
            animate={canScrollRight ? { x: [0, 3, 0] } : { x: 0 }}
            transition={{ duration: 1.6, repeat: canScrollRight ? Infinity : 0, ease: "easeInOut" }}
            className="flex items-center justify-center w-8 h-8 rounded-full bg-[#E8621A] text-white shadow-sm shadow-[#E8621A]/25"
          >
            <ChevronRight className="w-4 h-4" strokeWidth={2.75} />
          </motion.span>
        </motion.button>
      </div>

      {/* Composer teaser */}
      <Link
        to="/post"
        data-testid="composer-teaser"
        className="block bg-white rounded-3xl border border-[#1B2A4A]/8 p-5 hover:border-[#E8621A]/30 hover:shadow-md hover:shadow-[#1B2A4A]/5 transition-colors duration-200"
      >
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-[#E8621A]/10 flex items-center justify-center">
            <PlusCircle className="w-5 h-5 text-[#E8621A]" />
          </div>
          <div className="flex-1">
            <p className="text-[15px] text-[#1B2A4A] font-medium">Share a cost-saving tip with your community</p>
            <p className="text-xs text-[#1B2A4A]/50 mt-0.5">Every tip helps a neighbor breathe a little easier.</p>
          </div>
        </div>
      </Link>

      {/* Feed */}
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-[#E8621A]" />
        </div>
      ) : tips.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-3xl border border-dashed border-[#1B2A4A]/15">
          <p className="text-[#1B2A4A]/60">No tips in this category yet.</p>
          <Link to="/post">
            <Button className="mt-4 rounded-full bg-[#E8621A] hover:bg-[#D05615]" data-testid="empty-post-cta">
              Be the first to share
            </Button>
          </Link>
        </div>
      ) : (
        <motion.div className="space-y-4" initial="hidden" animate="visible" variants={{
          visible: { transition: { staggerChildren: 0.06 } },
          hidden: {},
        }}>
          {tips.map((t) => (
            <TipCard key={t.id} tip={t} onUpdate={load} />
          ))}
        </motion.div>
      )}
    </div>
  );
}
